import type {ReactNode} from 'react'

import type {SavedViewNavigation} from '../hooks/use-saved-view-navigation'
import type {Layout} from '../utils/saved-view-url-params'
import {SavedViewLayoutSkeleton} from './SavedViewLayoutSkeleton'

interface SavedViewLayoutState {
  /** Whether the route is loading a new response for this surface. */
  isNavigating: boolean
}

interface SavedViewLayoutSwitchProps {
  /** The layout of the route response on screen. */
  layout: Layout
  /** The loading navigation, from `useSavedViewNavigation`. */
  navigation: Pick<SavedViewNavigation, 'isNavigating' | 'pendingLayout' | 'layoutPlaceholder'>
  /** Renders the board for the response on screen. */
  renderBoard: (state: SavedViewLayoutState) => ReactNode
  /** Renders the list for the response on screen. */
  renderList: (state: SavedViewLayoutState) => ReactNode
}

/**
 * Renders a saved view in the layout of the response on screen, or a placeholder for a layout
 * that is loading.
 *
 * A layout change keeps the surface being left, busy, and then shows a placeholder rather than
 * the next surface. The results in hand are the layout being left, so the board would be handed
 * a list response and the list a board one. The list stays mounted and `inert` until then, so it
 * keeps its rows, scroll, and selection.
 */
export function SavedViewLayoutSwitch({
  layout,
  navigation: {isNavigating, pendingLayout, layoutPlaceholder},
  renderBoard,
  renderList,
}: SavedViewLayoutSwitchProps) {
  const isChangingLayout = pendingLayout !== null

  if (layoutPlaceholder !== null) return <SavedViewLayoutSkeleton layout={layoutPlaceholder} />
  if (layout === 'board') return renderBoard({isNavigating})
  return (
    <div inert={isChangingLayout || undefined}>{renderList({isNavigating: isNavigating && !isChangingLayout})}</div>
  )
}
